import type { AdminStoryListItem, AdminStoryStatus, ReviewFlag } from '@fonus/shared';
import { BookOpen, Plus } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, ListSkeleton, PageTitle } from '../components/common.tsx';
import { AlignMissing } from '../components/AlignMissing.tsx';
import { ErrorCard } from '../components/ErrorCard.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card } from '../components/ui/card.tsx';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs.tsx';
import { humanFromApi } from '../lib/errors.ts';
import { credits, money, SOURCE_LABEL, STATUS_LABEL, tashkent } from '../lib/format.ts';
import { useReadiness, useStories } from '../lib/queries.ts';
import { createBlocker } from '../lib/readiness.ts';
import { go } from '../lib/router.ts';

const ORDER: AdminStoryStatus[] = ['IN_REVIEW', 'DRAFT', 'SCHEDULED', 'PUBLISHED', 'ARCHIVED'];
const EMPTY_HINT: Record<AdminStoryStatus, string> = {
  IN_REVIEW: 'Здесь появятся сказки, когда они будут озвучены на всех трёх языках.',
  DRAFT: 'Черновик — это сказка, которая ещё пишется или озвучивается.',
  SCHEDULED: 'Одобренные сказки ждут здесь своего времени публикации.',
  PUBLISHED: 'Сказки, которые уже видят дети.',
  ARCHIVED: 'Сюда попадают снятые сказки.',
};

export function flagText(f: ReviewFlag): string {
  if (f.kind === 'words')
    return `${f.lang}: ${f.value < f.min ? 'мало' : 'много'} слов — ${String(f.value)} из ${String(f.value < f.min ? f.min : f.max)}`;
  if (f.kind === 'score') return `${f.lang}: оценка ${String(f.value)} < ${String(f.min)}`;
  return `похожее название: «${f.title}»`;
}

function Row({ s }: { s: AdminStoryListItem }) {
  return (
    <li>
      <Card className="transition hover:shadow-md">
        <button
          type="button"
          className="flex w-full items-center gap-4 p-4 text-left"
          onClick={() => go(`stories/${s.id}`)}
        >
          <span
            className="size-12 shrink-0 rounded-[14px]"
            style={{
              background: `linear-gradient(140deg, hsl(${String(s.hue)} 80% 78%), hsl(${String((s.hue + 40) % 360)} 70% 62%))`,
            }}
          />
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="flex flex-wrap items-center gap-2">
              <strong className="truncate text-[16px]">
                {s.titles.ru ?? s.titles.uz ?? s.slug}
              </strong>
              <Badge>{SOURCE_LABEL[s.sourceType]}</Badge>
              {s.busy && <Badge tone="accent">идёт задача…</Badge>}
            </span>
            <span className="truncate text-[13px] text-muted">
              {s.titles.uz} · озвучено {s.voiced.length}/3 · оценка {s.qualityScore ?? '—'}
            </span>
            {s.flags.length > 0 && (
              <span className="flex flex-wrap gap-1">
                {s.flags.map((f, i) => (
                  <Badge key={i} tone="warn">
                    {flagText(f)}
                  </Badge>
                ))}
              </span>
            )}
          </span>
          <span className="hidden shrink-0 text-right text-[13px] text-muted sm:block">
            {s.status === 'SCHEDULED'
              ? `выйдет ${tashkent(s.publishAt)}`
              : s.status === 'PUBLISHED'
                ? `вышла ${tashkent(s.publishedAt)}`
                : `создана ${tashkent(s.createdAt)}`}
            <br />
            {credits(s.cost.ttsCredits)} · {money(s.cost.money)}
          </span>
        </button>
      </Card>
    </li>
  );
}

export function Stories({ canEdit, isOwner }: { canEdit: boolean; isOwner: boolean }) {
  const all = useStories();
  const readiness = useReadiness();
  const fromHash = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get(
    'status',
  ) as AdminStoryStatus | null;
  const [chosen, setTab] = useState<AdminStoryStatus | null>(fromHash);
  // Open the first non-empty tab (owner request).
  const tab =
    chosen ?? (all.data ? (ORDER.find((s) => all.data.counts[s] > 0) ?? 'IN_REVIEW') : null);
  const blocker = createBlocker(readiness.data);
  const items = all.data?.items.filter((i) => i.status === tab) ?? [];

  return (
    <div>
      <PageTitle
        title="Сказки"
        actions={
          canEdit && (
            <div className="flex flex-col items-end gap-1">
              <Button disabled={blocker !== null} onClick={() => go('stories/new')}>
                <Plus /> Новая сказка
              </Button>
              {blocker && <span className="text-[12px] text-muted">{blocker}</span>}
            </div>
          )
        }
      />
      {all.error && <ErrorCard error={humanFromApi(all.error)} />}
      {canEdit && <AlignMissing isOwner={isOwner} />}
      {all.isLoading || !tab ? (
        <ListSkeleton />
      ) : (
        <>
          <Tabs value={tab} onValueChange={(v) => setTab(v as AdminStoryStatus)}>
            <TabsList aria-label="Статус">
              {ORDER.map((s) => (
                <TabsTrigger key={s} value={s}>
                  {STATUS_LABEL[s]} ({all.data?.counts[s] ?? 0})
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="mt-4">
            {items.length === 0 ? (
              <EmptyState
                icon={<BookOpen />}
                title={`«${STATUS_LABEL[tab]}» — пусто`}
                hint={EMPTY_HINT[tab]}
                action={
                  canEdit &&
                  !blocker && <Button onClick={() => go('stories/new')}>Создать сказку</Button>
                }
              />
            ) : (
              <ul className="flex flex-col gap-3">
                {items.map((s) => (
                  <Row key={s.id} s={s} />
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
